'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Clock, ArrowRight, Search, Tag } from 'lucide-react';

const CATEGORIES = ["All", "Training", "Nutrition", "Lifestyle", "Mindset"];

// Yahan humne props mein initialBlogs receive kar liya hai
export default function BlogClient({ initialBlogs = [] }) {
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  // useEffect aur loading state yahan se mukammal tor par khatam kar di gayi hai

  const filteredPosts = initialBlogs.filter((post) => {
    const matchesCategory = activeCategory === "All" || post.category === activeCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-[#050505] text-white py-16 px-4 md:px-8">
      {/* --- SEO ENHANCED HEADER --- */}
      <div className="max-w-7xl mx-auto mb-16 space-y-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center space-y-4"
        >
          <h1 className="text-4xl md:text-6xl font-bold text-[#faaf40] tracking-tight">
            The Aura Journal
          </h1>
          {/* SEO Paragraph with target keywords */}
          <p className="text-gray-400 max-w-3xl mx-auto text-sm md:text-base leading-relaxed">
            Welcome to the official blog of the <strong className="text-gray-300 font-medium">top gym in Islamabad</strong>. 
            Explore expert insights on EMS training, nutrition, and watch the <strong className="text-gray-300 font-medium">best transformation videos</strong> from our elite F-6 sanctuary.
          </p>
        </motion.div>

        {/* --- CONTROLS --- */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-6 border-b border-[#faaf40]/20 pb-8">
          <div className="flex flex-wrap gap-2 justify-center md:justify-start">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeCategory === cat
                    ? "bg-[#faaf40] text-black shadow-[0_0_15px_rgba(250,175,64,0.4)]"
                    : "bg-[#0f0f0f] text-gray-400 border border-white/10 hover:border-[#faaf40]/50 hover:text-[#faaf40]"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="relative w-full md:w-72">
            <input
              type="text"
              placeholder="Search fitness articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-[#0f0f0f] border border-white/10 rounded-full py-2.5 pl-10 pr-4 text-sm text-gray-200 focus:outline-none focus:border-[#faaf40] transition-colors"
              aria-label="Search blogs"
            />
            <Search className="absolute left-3.5 top-2.5 text-gray-500 w-4 h-4" />
          </div>
        </div>
      </div>

      {/* --- BLOG GRID --- */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        
        {/* Loading condition hata di hai, ab direct AnimatePresence render hoga */}
        <AnimatePresence mode='popLayout'>
          {filteredPosts.map((post) => (
            <BlogCard key={post._id} post={post} />
          ))}
        </AnimatePresence>

        {filteredPosts.length === 0 && (
          <div className="col-span-full text-center py-20 text-gray-500">
            <p>No articles found matching your criteria.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// SEO Enhanced Blog Card using <article> tag
function BlogCard({ post }) {
  const formattedDate = new Date(post.date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.3 }}
      className="h-full"
    >
      {/* Changed route to /blogs/ for consistency */}
      <Link href={`/blogs/${post.slug || post._id}`} className="block h-full" title={post.title}>
        <article className="group bg-[#0f0f0f]/80 border border-[#faaf40]/20 rounded-2xl overflow-hidden hover:border-[#faaf40] transition-all duration-500 hover:shadow-[0_0_30px_-10px_rgba(250,175,64,0.3)] flex flex-col h-full cursor-pointer">
          
          <div className="relative h-48 md:h-56 overflow-hidden bg-gray-900">
            <div className="absolute top-4 left-4 z-10 bg-black/60 backdrop-blur-md px-3 py-1 rounded text-[10px] font-bold uppercase tracking-wider text-[#faaf40] flex items-center gap-1 border border-[#faaf40]/20">
              <Tag size={10} />
              {post.category || 'General'}
            </div>
            
            <div className="relative w-full h-full">
              {post.image ? (
                <Image
                  src={post.image} 
                  alt={post.title}
                  fill
                  unoptimized={true}
                  className="object-cover group-hover:scale-110 transition-transform duration-700"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-600 italic text-sm bg-[#111]">
                  Aura Wellness Content
                </div>
              )}
            </div>
          </div>

          <div className="p-6 flex flex-col flex-grow">
            <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
              <div className="flex items-center gap-1">
                <Calendar size={12} className="text-[#faaf40]" />
                <time dateTime={post.date}>{formattedDate}</time>
              </div>
              <div className="flex items-center gap-1">
                <Clock size={12} className="text-[#faaf40]" />
                <span>{post.readTime || '5 min read'}</span>
              </div>
            </div>

            <h2 className="text-xl font-bold text-white mb-3 group-hover:text-[#faaf40] transition-colors line-clamp-2">
              {post.title}
            </h2>
            <p className="text-gray-400 text-sm leading-relaxed mb-6 line-clamp-3 flex-grow">
              {post.excerpt}
            </p>

            <div className="flex items-center gap-2 text-sm font-medium text-[#faaf40] group-hover:gap-3 transition-all">
              Read Article <ArrowRight size={16} />
            </div>
          </div>
        </article>
      </Link>
    </motion.div>
  );
}